import { MockTransport } from '@/transport/mock/mockTransport';
/**
 * Shown the moment the panel opens, entirely client-side. Split into
 * paragraphs so the closing question lands as its own line — the renderer
 * gives a trailing question its own emphasis.
 */
export const DEFAULT_GREETING = {
    text: 'Hi! 👋 Welcome to AlignBooks.\n\n' +
        'I can help you explore our features, pricing, integrations, and find the ' +
        'right solution for your business.\n\n' +
        'How can I help?',
    suggestions: ['Explore features', 'See pricing', 'Integrations', 'Find the right fit'],
};
/**
 * Default avatar, served from `public/`. Root-relative on purpose: the
 * standalone app is same-origin with it, and the embed resolves it against
 * the origin its own script came from (see widget/index.ts), so it doesn't
 * point at the host website.
 */
export const DEFAULT_AVATAR_PATH = '/caira_avatar.png';
/** Default chat-button image. Same file and same resolution rules as the avatar. */
export const DEFAULT_LAUNCHER_ICON_PATH = '/caira_avatar.png';
/**
 * 10 then 2, against the backend's 15 minutes of silence: the visitor gets two
 * minutes to answer the warning, and the chat locks with three minutes to spare
 * — enough that a slow clock or a backgrounded tab cannot make the notice a lie.
 */
export const DEFAULT_INACTIVITY = {
    enabled: true,
    warnAfterMs: 10 * 60_000,
    closeAfterWarningMs: 2 * 60_000,
};
export const DEFAULT_COPY = {
    composerPlaceholder: 'Ask a question…',
    awaitingReply: 'Please wait for the reply…',
    headerBack: 'Close',
    send: 'Send',
    retry: 'Try again',
    sourcesLabel: 'Sources',
    typing: 'is thinking',
    streaming: 'typing',
    aiLabel: 'AI assistant',
    humanLabel: 'Support team',
    progressSteps: [
        '✨ Understanding your request…',
        '🔍 Analyzing the query…',
        '💭 Thinking & planning…',
        '📚 Checking ERP knowledge…',
        '🗂️ Identifying relevant data…',
        '🔎 Searching knowledge base…',
        '📊 Analyzing the results…',
        '🧮 Calculating required figures…',
        '🔗 Connecting related information…',
        '✅ Validating the results…',
        '✍️ Preparing your answer…',
    ],
    statusStages: {
        understanding: '✨ Reading your question…',
        planning: '💭 Working out the best answer…',
        searching: '🔎 Looking that up for you…',
        // The data assistant querying the client's own books.
        analysing: '📊 Working through your numbers…',
        writing: '✍️ Writing your answer…',
        // A person in the console is writing: the same dots, no "thinking" lines.
        typing: 'Typing…',
    },
    errorGeneric: 'Something went wrong.',
    feedbackPrompt: 'Was this helpful?',
    feedbackThanks: 'Thanks for the feedback.',
    newConversation: 'Start a new chat',
    poweredBy: 'Powered by AgenticXLab',
    poweredByUrl: 'https://agenticxlab.com/',
    inactivityWarningTitle: 'Are you still there?',
    inactivityWarningBody: 'We haven’t received a response for a while. This chat will close in {minutes} ' +
        'due to inactivity. You can always start a new chat when you’re ready.',
    inactivityClosedTitle: 'Chat closed due to inactivity',
    inactivityClosedBody: 'No worries! You can start a new chat anytime when you’re ready. ' +
        'We’ll be happy to help you again.',
    inactivityRestart: 'Start a fresh chat',
    endedTitle: 'This chat has ended',
    endedBody: 'Our team has closed this conversation. Start a new chat any time you need us.',
    privateNotice: 'Private — only you can see this chat. Answers come from your company’s own data.',
    backToChat: 'Support chat',
    expand: 'Expand',
    collapse: 'Normal size',
    fullScreen: 'Full screen',
    exitFullScreen: 'Exit full screen',
    pastChats: 'Past chats',
    pastChatsEmpty: 'Your past questions about this account will appear here.',
    pastChatsError: 'Couldn’t load your past chats. Try again in a moment.',
    newChat: 'New chat',
    deleteChat: 'Delete chat',
    untitledChat: 'Untitled chat',
    back: 'Back to the chat',
    home: 'Home',
    messages: 'Messages',
    askQuestion: 'Ask a question',
    noMessages: 'No messages yet',
};
/** AgenticXLab terracotta (agenticxlab docs/brand.md). */
export const DEFAULT_PRIMARY = '#d97757';
/** The brand's ink: text on a light accent. */
const INK = '#141413';
/**
 * White or ink, whichever contrasts more with `color`. Only `#rgb` and
 * `#rrggbb` can be measured; any other CSS colour gets white, as before.
 */
export function readableOn(color) {
    const hex = color.trim().replace(/^#/, '');
    if (!/^([0-9a-f]{3}|[0-9a-f]{6})$/i.test(hex))
        return '#ffffff';
    const full = hex.length === 3 ? [...hex].map((c) => c + c).join('') : hex;
    const [r, g, b] = [0, 2, 4].map((i) => {
        const channel = parseInt(full.slice(i, i + 2), 16) / 255;
        return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
    });
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    const inkLuminance = 0.0069;
    const onWhite = 1.05 / (luminance + 0.05);
    const onInk = (luminance + 0.05) / (inkLuminance + 0.05);
    return onInk > onWhite ? INK : '#ffffff';
}
export const DEFAULT_HOME = {
    enabled: true,
    headline: ['Hello there.', 'How can we help?'],
    cards: [{ type: 'ask', title: 'Ask a question', body: 'Our AI assistant can help' }],
    avatars: [],
};
/**
 * The Home screen, or undefined when it is off. Cards a page got wrong (no
 * title, a link without a usable URL) are dropped with no fuss rather than
 * rendering broken tiles on a sales site.
 */
export function resolveHome(input, mode) {
    if (!input || input.enabled === false || mode === 'data')
        return undefined;
    const cards = (input.cards ?? DEFAULT_HOME.cards)
        .filter((card) => {
        if (!card?.title?.trim())
            return false;
        if (card.type === 'link')
            return !!safeUrl(card.url);
        return card.type === 'ask' || card.type === 'prompt';
    })
        // A banner colour goes into a style attribute: hex only.
        .map((card) => (card.color && !HEX_COLOR.test(card.color) ? { ...card, color: undefined } : card));
    return {
        enabled: true,
        headline: input.headline?.filter((line) => line?.trim()) ?? DEFAULT_HOME.headline,
        cards,
        avatars: (input.avatars ?? []).map(safeUrl).filter((url) => !!url).slice(0, 3),
    };
}
const HEX_COLOR = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
/** `https:`, `http:` or a site path; never `javascript:` and friends. */
export function safeUrl(url) {
    const value = url?.trim();
    if (!value)
        return undefined;
    if (value.startsWith('/') && !value.startsWith('//'))
        return value;
    return /^https?:\/\//i.test(value) ? value : undefined;
}
/** Single place where every default lives; deep-merged with user input. */
export function resolveConfig(input = {}) {
    return {
        transport: input.transport ?? new MockTransport(),
        locale: input.locale ?? 'en',
        context: input.context,
        greeting: input.greeting ?? DEFAULT_GREETING,
        home: resolveHome(input.home, input.mode),
        presentation: input.presentation ?? 'panel',
        serverStatus: input.serverStatus ?? true,
        openOnLoad: input.openOnLoad ?? false,
        persistenceTtlMs: input.persistenceTtlMs ?? 24 * 60 * 60 * 1000,
        logLevel: input.logLevel,
        onLog: input.onLog,
        mode: input.mode ?? 'chat',
        storageNamespace: input.storageNamespace,
        onOpenDataMode: input.onOpenDataMode,
        onExitDataMode: input.onExitDataMode,
        branding: {
            ...input.branding,
            // Never blank: the header title and the avatar's initials derive from it.
            name: input.branding?.name?.trim() || 'CAIRA',
            // Missing or null keeps the default; '' deliberately hides it.
            statusLabel: input.branding?.statusLabel ?? 'Replies instantly',
            avatarUrl: input.branding?.avatarUrl ?? DEFAULT_AVATAR_PATH,
        },
        theme: {
            primary: DEFAULT_PRIMARY,
            radius: '16px',
            colorScheme: 'auto',
            ...input.theme,
            // Missing: whichever of white or ink reads better on the accent, so a
            // light accent gets dark text and a dark one white.
            primaryText: input.theme?.primaryText || readableOn(input.theme?.primary || DEFAULT_PRIMARY),
        },
        launcher: {
            enabled: true,
            position: 'bottom-right',
            offset: 20,
            label: 'Chat with us',
            showUnreadBadge: true,
            ...input.launcher,
            // Missing or null keeps the default; '' deliberately means the built-in icon.
            iconUrl: input.launcher?.iconUrl ?? DEFAULT_LAUNCHER_ICON_PATH,
        },
        features: {
            sources: true,
            feedback: true,
            suggestions: true,
            handoff: true,
            attachments: false,
            persistence: true,
            emphasizeQuestions: true,
            soundOnReply: false,
            authorLabels: true,
            expand: input.mode === 'data',
            history: input.mode === 'data',
            ...input.features,
        },
        copy: {
            ...DEFAULT_COPY,
            ...input.copy,
            // Per stage, so overriding one line keeps the defaults for the rest.
            statusStages: { ...DEFAULT_COPY.statusStages, ...input.copy?.statusStages },
        },
        inactivity: { ...DEFAULT_INACTIVITY, ...input.inactivity },
    };
}
